/**
 * SW2-2244 — the one conversion from a stored date-only value to the `Date` a
 * date picker must be given so it displays that same calendar day.
 *
 * ## Why this exists
 *
 * Date-only fields (project start/end, award dates, finding dates) are stored
 * as midnight UTC — `2026-08-18 00:00:00+00`. That is a property of the
 * deployment, not a coincidence: the API runs under `TZ=Etc/GMT`
 * (`backend/.env.default`), so the `MM-dd-yyyy` string the picker sends is
 * parsed at UTC midnight. Handing that instant straight to a picker renders it
 * in the viewer's timezone, which lands on the previous day for anyone behind
 * UTC:
 *
 *     new Date('2026-08-18T00:00:00.000Z')   // Aug 17, 20:00 in Denver
 *
 * The picker then shows 08/17/2026 for a value that is stored as the 18th, and
 * saving that display walks the stored date back a day. The frontend had four
 * hand-rolled idioms for avoiding this — offset arithmetic, `getUTC*`
 * reconstruction, `'T00:00:00'` string append, and raw `new Date()` — three
 * correct and one not, with nothing at the call site to tell them apart. All of
 * them look correct in IST (UTC+5:30), which is why the broken one survived.
 *
 * ## The contract
 *
 * Give it a *stored* value — what the API returned. It answers with local
 * midnight on the calendar day that value denotes, which is what every picker
 * in the app wants. It is deliberately not the inverse of "format for the
 * server": the write path sends the picker's own `MM-dd-yyyy` string.
 *
 * A value carrying a time-of-day is read as its **UTC** calendar day, matching
 * how these columns are written and preserving `parseYMD`'s long-standing
 * behaviour on full timestamps.
 */

export type CalendarDateInput = string | number | Date | null | undefined;

/** `YYYY-M-D` at the head of a string: date-only, ISO instant, or `YYYY-MM-DD HH:mm:ss`. */
const YMD_PREFIX = /^(\d{4})-(\d{1,2})-(\d{1,2})/;

/** `MM-dd-yyyy` — what lib-form's date control emits — and its slashed variant. */
const MDY_ONLY = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;

/**
 * Build local midnight from calendar parts, rejecting parts that are not a real
 * date. `new Date(2026, 12, 1)` silently rolls into January, so round-trip the
 * result rather than trusting the constructor.
 */
function fromParts(year: number, month: number, day: number): Date | null {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

/**
 * The calendar day a stored date-only value denotes, as local midnight.
 *
 * @param value a stored value: `'2026-08-18'`, `'2026-08-18T00:00:00.000Z'`,
 *   `'2026-08-18 00:00:00'`, `'08-18-2026'`, a `Date`, or an epoch number.
 * @returns local midnight on that day, or `null` for empty/unparseable input.
 *
 * @example
 * // Displays as 08/18/2026 in every timezone, not 08/17 west of UTC.
 * toCalendarDate('2026-08-18T00:00:00.000Z');
 */
export function toCalendarDate(value: CalendarDateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date || typeof value === 'number') {
    const instant = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(instant.getTime())) return null;

    return fromParts(
      instant.getUTCFullYear(),
      instant.getUTCMonth() + 1,
      instant.getUTCDate(),
    );
  }

  if (typeof value !== 'string') return null;

  const text = value.trim();

  if (!text) return null;

  // Read the calendar parts out of the string itself wherever they are already
  // there. Going through `new Date()` first would reintroduce the very
  // timezone shift this function exists to remove, and would additionally read
  // `'2026-08-18 00:00:00'` as local rather than UTC.
  const ymd = YMD_PREFIX.exec(text);

  if (ymd) return fromParts(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]));

  const mdy = MDY_ONLY.exec(text);

  if (mdy) return fromParts(Number(mdy[3]), Number(mdy[1]), Number(mdy[2]));

  // Anything else — a display string like 'August 18, 2026'. Nothing in the app
  // stores dates this way; the branch is defensive. Such strings have no zone,
  // so the platform parses them as local and the local parts are the day that
  // was written.
  const parsed = new Date(text);

  if (Number.isNaN(parsed.getTime())) return null;

  return fromParts(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate(),
  );
}

/**
 * SW2-2439 — whether `value` falls on an earlier calendar day than `reference`,
 * both read the way `toCalendarDate` reads them.
 *
 * Use this instead of comparing instants whenever either side is a date-only
 * value. A completion date stored as `2026-09-30T00:00:00.000Z` is the evening
 * of the 29th to `moment()` in any US timezone, so an instant comparison against
 * an inspection dated 2026-09-30 calls a same-day close "before".
 *
 * Reading both sides as their UTC calendar day also matches the API, which runs
 * under `TZ=Etc/GMT` and unlinks a carried-over finding from every inspection
 * dated after its completion day (`findingService.updateInspFindingsCarryOver`).
 *
 * @returns `false` when either side is empty or unparseable.
 */
export function isCalendarDayBefore(
  value: CalendarDateInput,
  reference: CalendarDateInput,
): boolean {
  const day = toCalendarDate(value);
  const referenceDay = toCalendarDate(reference);

  if (!day || !referenceDay) return false;

  return day.getTime() < referenceDay.getTime();
}
